/**
 * Утилиты работы с цветом для генератора тем (sRGB-микс, светлота, контраст).
 *
 * Намеренно простые операции в sRGB: их достаточно, чтобы из одного акцентного
 * цвета вывести hover/soft-варианты и подобрать читаемый foreground. Точность
 * перцептивных пространств (oklch) здесь не нужна — палитра нейтралей задаётся
 * готовыми шкалами Tailwind (см. palettes.ts).
 */

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

const clampByte = (v: number): number =>
  Math.max(0, Math.min(255, Math.round(v)));

/** Разобрать hex (`#rgb` / `#rrggbb`) в RGB. Невалидный вход → чёрный. */
export const parseHex = (hex: string): Rgb => {
  const raw = hex.trim().replace(/^#/, '');
  const full =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw.padEnd(6, '0').slice(0, 6);
  const num = Number.parseInt(full, 16);
  if (Number.isNaN(num)) return {r: 0, g: 0, b: 0};
  return {r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255};
};

/** Собрать RGB в `#rrggbb`. */
export const toHex = ({r, g, b}: Rgb): string => {
  const part = (v: number) => clampByte(v).toString(16).padStart(2, '0');
  return `#${part(r)}${part(g)}${part(b)}`;
};

/** Линейный микс двух цветов в sRGB: `t=0` → a, `t=1` → b. */
export const mix = (a: string, b: string, t: number): string => {
  const ca = parseHex(a);
  const cb = parseHex(b);
  const k = Math.max(0, Math.min(1, t));
  return toHex({
    r: ca.r + (cb.r - ca.r) * k,
    g: ca.g + (cb.g - ca.g) * k,
    b: ca.b + (cb.b - ca.b) * k,
  });
};

/** Осветлить (подмешать белого). */
export const lighten = (hex: string, amount: number): string =>
  mix(hex, '#ffffff', amount);

/** Затемнить (подмешать чёрного). */
export const darken = (hex: string, amount: number): string =>
  mix(hex, '#000000', amount);

/** Относительная светлота (WCAG-приближение, 0..1). */
export const relativeLuminance = (hex: string): number => {
  const {r, g, b} = parseHex(hex);
  const channel = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

/** Читаемый текст поверх фона: белый или почти-чёрный по светлоте фона. */
export const readableForeground = (bg: string): string =>
  relativeLuminance(bg) > 0.5 ? '#0a0a0a' : '#ffffff';
